// paletteFromImage.ts — derive a palette from ANY uploaded image via deterministic median-cut
// (#103 follow-up). The portal's "🎨 From image" control extracts up to `count` representative
// colours from whatever image the user uploaded to the console (a reference photo, artwork,
// screenshot — NOT necessarily a camera capture). Colours are histogrammed at the camera's RGB565
// precision (the grid the camera can actually display), so the colour count maps to genuinely
// distinct output colours and a box's budget is never wasted separating colours that look
// identical once shown (e.g. 248 and 252 both snap to 248). It is a COLOUR-WEIGHTED median-cut:
// vivid pixels count more (so colourful regions earn more of the palette than the muted majority
// that covers most of a photo's area would otherwise grab), and each box then contributes its most
// VIVID colour (the highest-chroma entry holding ≥3% of the box — never a muddy average), so a
// vibrant source yields a vibrant palette. Distinct boxes always yield distinct colours (no silent
// under-fill). Every colour round-trips through rgbaToRGB565 unchanged (the editor chip matches the
// preview). Deterministic — no Math.random — so it is host-testable like the rest of src/cores.

import { rgbLuminanceInt, MAX_COLORS_PER_PALETTE } from "./dither";

/** Colour-vibrance bias (0 = plain median-cut). A fully-saturated pixel counts (1 + VIBRANCE) as
 *  much as a grey one, so colourful regions earn more of the palette than a muted majority would. */
const VIBRANCE = 6;

/** One distinct 565-snapped colour: `n` is its colour-weighted presence (vivid pixels weigh more —
 *  drives selection/split/representative); `c` is its RAW pixel count (drives the noise floor). */
interface Entry {
  key: number; // (r<<16)|(g<<8)|b — unique per snapped colour; also the deterministic sort key
  r: number;
  g: number;
  b: number;
  n: number;
  c: number;
}

const hex2 = (n: number): string => n.toString(16).padStart(2, "0");
const toHex = (r: number, g: number, b: number): string => `#${hex2(r)}${hex2(g)}${hex2(b)}`;

/** Luminance of a "#rrggbb" colour, via the engine's integer weighting (for ordering). */
function lumOfHex(hex: string): number {
  const n = hex.slice(1);
  return rgbLuminanceInt(parseInt(n.slice(0, 2), 16), parseInt(n.slice(2, 4), 16), parseInt(n.slice(4, 6), 16));
}

/** "Colourfulness" (chroma), 0..1 — bright + saturated scores highest, so the representative pick
 *  favours vivid neon over both greys and dark muddy colours. */
function colorfulness(r: number, g: number, b: number): number {
  return (Math.max(r, g, b) - Math.min(r, g, b)) / 255;
}

/** The value of entry `e` on channel `ch` (0=R, 1=G, 2=B). */
function chan(e: Entry, ch: number): number {
  return ch === 0 ? e.r : ch === 1 ? e.g : e.b;
}

/** Collapse the source into distinct colour entries at the camera's RGB565 precision (transparent
 *  pixels skipped). Binning at 565 — what the camera can display — is what keeps median-cut's
 *  notion of "distinct" the SAME as the output's, so the budget is spent on real output colours. */
function buildHistogram(rgba: Uint8ClampedArray | Uint8Array, w: number, h: number): Entry[] {
  const byKey = new Map<number, Entry>();
  const px = w * h;
  for (let i = 0; i < px; i++) {
    if ((rgba[i * 4 + 3] ?? 255) === 0) continue; // transparent: never inject black
    const r = (rgba[i * 4]! >> 3) << 3;       // snap to the RGB565 grid (matches rgbaToRGB565)
    const g = (rgba[i * 4 + 1]! >> 2) << 2;
    const b = (rgba[i * 4 + 2]! >> 3) << 3;
    const wgt = 1 + VIBRANCE * colorfulness(r, g, b); // colourful pixels carry more weight
    const key = (r << 16) | (g << 8) | b;
    const e = byKey.get(key);
    if (e) { e.n += wgt; e.c++; }
    else byKey.set(key, { key, r, g, b, n: wgt, c: 1 });
  }
  return [...byKey.values()];
}

/** Median-cut: split the (key-sorted) entries into ≤count boxes, each a slice of `arr`. */
function medianCut(entries: Entry[], count: number): Entry[][] {
  const arr = entries.slice(); // caller pre-sorts by key for determinism
  const boxes: Array<{ lo: number; hi: number }> = [{ lo: 0, hi: arr.length }];

  const range = (lo: number, hi: number, ch: number): number => {
    let mn = 255, mx = 0;
    for (let i = lo; i < hi; i++) {
      const v = chan(arr[i]!, ch);
      if (v < mn) mn = v;
      if (v > mx) mx = v;
    }
    return mx - mn;
  };

  const weight = (lo: number, hi: number): number => {
    let s = 0;
    for (let i = lo; i < hi; i++) s += arr[i]!.n;
    return s;
  };

  while (boxes.length < count) {
    // Split the box with the largest (colour-weighted mass × spread): the mass is colour-weighted
    // (vivid pixels count more — see buildHistogram), so colourful regions earn more splits than
    // the muted majority; the spread term (largest channel range, ties R>G>B) avoids subdividing
    // near-flat boxes into near-duplicate colours.
    let best = -1, bestScore = 0, bestCh = 0;
    for (let bi = 0; bi < boxes.length; bi++) {
      const box = boxes[bi]!;
      if (box.hi - box.lo < 2) continue;
      const rr = range(box.lo, box.hi, 0);
      const rg = range(box.lo, box.hi, 1);
      const rb = range(box.lo, box.hi, 2);
      let ch = 0, chR = rr;
      if (rg > chR) { ch = 1; chR = rg; }
      if (rb > chR) { ch = 2; chR = rb; }
      if (chR === 0) continue; // a box with no colour spread can't be meaningfully split
      const score = chR * weight(box.lo, box.hi);
      if (score > bestScore) { bestScore = score; best = bi; bestCh = ch; }
    }
    if (best < 0 || bestScore === 0) break; // nothing left to split

    const box = boxes[best]!;
    // sort the box's slice by the chosen channel; ties fall through to key (a total order)
    const slice = arr.slice(box.lo, box.hi).sort((a, b) => chan(a, bestCh) - chan(b, bestCh) || a.key - b.key);
    for (let i = 0; i < slice.length; i++) arr[box.lo + i] = slice[i]!;
    // population-weighted median: split where cumulative n first reaches half the box total
    let total = 0;
    for (let i = box.lo; i < box.hi; i++) total += arr[i]!.n;
    let acc = 0, mid = box.lo + 1;
    for (let i = box.lo; i < box.hi; i++) {
      acc += arr[i]!.n;
      if (acc * 2 >= total) { mid = i + 1; break; }
    }
    if (mid <= box.lo) mid = box.lo + 1;
    if (mid >= box.hi) mid = box.hi - 1;
    boxes.splice(best, 1, { lo: box.lo, hi: mid }, { lo: mid, hi: box.hi });
  }

  return boxes.map((b) => arr.slice(b.lo, b.hi));
}

/** A box's representative = its most VIVID colour (highest chroma) among entries holding ≥3% of the
 *  box — so a single-pixel outlier can't hijack it, but a genuine punchy accent beats a muted
 *  majority. Falls back to the most-present entry when nothing clears the floor. Ties → more
 *  present, then lower key (determinism). Always a real entry, so distinct boxes ⇒ distinct
 *  colours (no under-fill); already 565-snapped. */
function boxColor(box: Entry[]): string {
  let pixels = 0, top = box[0]!;
  for (const e of box) { pixels += e.c; if (e.n > top.n || (e.n === top.n && e.key < top.key)) top = e; }
  const floor = pixels * 0.03; // ≥3% of the box's RAW pixels — a single vivid outlier can't hijack it
  let best = top, bestChroma = colorfulness(top.r, top.g, top.b);
  for (const e of box) {
    if (e.c < floor) continue;
    const ch = colorfulness(e.r, e.g, e.b);
    if (ch > bestChroma || (ch === bestChroma && (e.n > best.n || (e.n === best.n && e.key < best.key)))) {
      best = e; bestChroma = ch;
    }
  }
  return toHex(best.r, best.g, best.b);
}

/**
 * Derive up to `count` representative "#rrggbb" colours from an RGBA source via deterministic
 * median-cut over the camera's RGB565 histogram. Each colour is a legal RGB565 value (it
 * round-trips through rgbaToRGB565 unchanged). Ordered ascending by luminance (dark→light).
 * Returns 1..count colours for any non-empty opaque source; fewer than `count` only when the
 * image has fewer distinct 565 colours; [] only for an empty / fully-transparent source.
 */
export function paletteFromImage(
  rgba: Uint8ClampedArray | Uint8Array,
  w: number,
  h: number,
  count: number,
): string[] {
  const n = Math.max(1, Math.min(Math.trunc(count) || 1, MAX_COLORS_PER_PALETTE));
  const entries = buildHistogram(rgba, w, h);
  if (entries.length === 0) return [];
  entries.sort((a, b) => a.key - b.key); // deterministic working order, independent of Map iteration
  const boxes = medianCut(entries, n);
  const seen = new Set<string>();
  const colors: string[] = [];
  for (const box of boxes) {
    const hex = boxColor(box);
    if (!seen.has(hex)) { seen.add(hex); colors.push(hex); } // defensive — disjoint boxes already yield distinct colours
  }
  colors.sort((a, b) => lumOfHex(a) - lumOfHex(b) || (a < b ? -1 : a > b ? 1 : 0));
  return colors;
}
